import { GetTripPassengerResponse, TripDetailCalendarDto, TripLocationDto, TripPassengerModel, TripSeatingModel } from '@/__generated';
import { t } from 'i18next';
import { ROW_GUTTER } from '@/config';
import { apiSdk } from '@/httpClient';
import { Col, Row, Select, Space, Spin, Table } from 'antd';
import React from 'react';
import { sortBy } from 'lodash';
import SeatingModelDetail from '@/components/app/BusSeatingModelDetail/SeatingModelDetail';
import { getFullName } from '@/helpers/string';
import { ColumnsType } from 'antd/lib/table';

type Props = {
  trip: TripDetailCalendarDto;
};

const columns: ColumnsType<TripPassengerModel> = [
  {
    get title() { return t('Name'); },
    render: (v, r) => getFullName(r.firstName, r.lastName),
  },
  {
    get title() { return t('Seat'); },
    dataIndex: 'seatNo',
  },
  {
    get title() { return t('Source'); },
    dataIndex: 'sourceName',
  },
  {
    get title() { return t('Destination'); },
    dataIndex: 'destinationName',
  },
];
export default function SeatingInfoTab({ trip }: Props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [tripLocations, setTripLocations] = React.useState<TripLocationDto[]>([]);

  const [seatingModel, setSeatingModel] = React.useState<TripSeatingModel>();
  const [passengers, setPassengers] = React.useState<GetTripPassengerResponse>();

  const [fromStationId, setFromStationId] = React.useState<string>();
  const [toStationId, setToStationId] = React.useState<string>();

  React.useEffect(() => {
    if (trip.id) {
      apiSdk.TripLocationService.getTripDetailsLocations({ tripDetailId: trip.id || '' })
        .then(res => {
          setTripLocations(res.locations || []);
        })
        .finally(() => {
          setIsLoading(false);
        });
      apiSdk.BookingPassengerService.getTripPassengers({ tripId: trip.id }).then(res => {
        setPassengers(res);
      });
    }
  }, [trip.id]);

  const stations = React.useMemo(() => {
    return sortBy(
      tripLocations.filter(x => x.stationId),
      x => x.order,
    ).map(s => ({
      stationId: s.stationId,
      value: s.stationId,
      label: s.name,
    }));
  }, [tripLocations]);

  React.useEffect(() => {
    if (stations.length) {
      setFromStationId(stations[0].stationId || undefined);
      setToStationId(stations[stations.length - 1].stationId || undefined);
    }
  }, [stations]);

  React.useEffect(() => {
    if (fromStationId && trip && toStationId) {
      setIsLoading(true);
      apiSdk.TripSearchService.getTripSeatingModel({
        body: {
          tripId: trip.id,
          sourceStationId: fromStationId,
          destinationStationId: toStationId,
        },
      })
        .then(res => {
          setSeatingModel(res);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [trip, fromStationId, toStationId]);

  return (
    <Spin spinning={isLoading}>
      <div style={{ textAlign: 'center' }}>
        <Space direction="horizontal" align="center">
          <span>{t('From')}</span>
          <Select value={fromStationId} options={stations} style={{ minWidth: 200 }} onChange={v => setFromStationId(v)} />
          <span>To</span>
          <Select options={stations} value={toStationId} style={{ minWidth: 200 }} onChange={v => setToStationId(v)} />
        </Space>
      </div>
      <Row gutter={ROW_GUTTER} style={{ marginTop: 10 }}>
        <Col md={12}>{seatingModel ? <SeatingModelDetail seatingModel={seatingModel} /> : <div style={{ minHeight: 400 }}></div>}</Col>
        <Col md={12}>
          <Table title={() => <b>{t('Schedule Passengers')}</b>} size="small" columns={columns} dataSource={passengers?.schedulePassengers || []} />
          <Table title={() => <b>{t('Sell On The Go Passengers')}</b>} size="small" columns={columns} dataSource={passengers?.sellOnTheGoPassengers || []} />
        </Col>
      </Row>
    </Spin>
  );
}
